import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, FileText, Loader2 } from 'lucide-react';
import { documentsService, Document } from '@/features/documents/services/documents.service';

export const Route = createFileRoute('/_authenticated/important-documents')({
  component: ImportantDocumentsPage,
});

function ImportantDocumentsPage() {
  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      const response = await documentsService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const documents: Document[] = documentsData || [];

  const handleDownload = async (document: Document) => {
    // Registrar acesso antes de abrir o link
    try {
      await documentsService.recordAccess(document.id);
    } catch (error) {
      // Não bloquear o download se falhar
      console.error('Erro ao registrar acesso:', error);
    }

    // Abrir link do documento
    if (document.downloadUrl) {
      window.open(document.downloadUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Documentos Importantes</h1>
          <p className="text-muted-foreground mt-2">
            Nesta área você encontra documentos essenciais, manuais e guias importantes para o uso do
            sistema e processos da empresa. Todos os documentos estão disponíveis para download.
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Documentos Importantes</h1>
        <p className="text-muted-foreground mt-2">
          Nesta área você encontra documentos essenciais, manuais e guias importantes para o uso do
          sistema e processos da empresa. Todos os documentos estão disponíveis para download.
        </p>
      </div>

      {documents.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {documents.map((document) => (
            <Card key={document.id} className="flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-lg">{document.name}</CardTitle>
                  </div>
                </div>
                {document.description && (
                  <CardDescription className="mt-2">{document.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-end">
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    {document.fileSize && <span>Tamanho: {document.fileSize}</span>}
                    {document.updatedAt && (
                      <span>
                        Atualizado em:{' '}
                        {new Date(document.updatedAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={() => handleDownload(document)}
                    className="w-full"
                    variant="default"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Baixar Documento
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhum documento disponível no momento.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
